import { ChangeEvent, Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';

import {
  Button,
  CheckCircleFillIcon,
  DatePicker,
  Divider,
  Input,
  Radio,
  RadioGroup,
  Text,
  WarningFillIcon,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';

import { Bullet, FormField } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { useGetOwnerSupplierBusinessNumberCheck } from '~/hooks/vender';
import { useLoadingStore } from '~/store';
import { formatBusinessNumber, validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS, TOAST_MESSAGE } from '~/constants';

import { type TypeVenderSupplierDetailErrorFieldRefs } from '../VenderSupplierDetailModal/VenderSupplierDetailModal';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeSupplierDetailFormStatus, TypeSupplierDetailFormValue } from '~/types/vender.types';

import styles from './VenderSupplierDetailModalDefaultInfoSection.module.scss';

type TypeVenderSupplierDetailModalDefaultInfoSectionProps = {
  errorFieldRefs: MutableRefObject<
    Pick<TypeVenderSupplierDetailErrorFieldRefs, 'businessNumber' | 'supplierName' | 'tel' | 'fax'>
  >;
  ownerSupplierCode: string;
  venderSupplierDetailForm: TypeSupplierDetailFormValue;
  venderSupplierDetailFormStatus: TypeSupplierDetailFormStatus;
  isBusinessNumberValid: boolean | null;
  setVenderSupplierDetailForm: Dispatch<SetStateAction<TypeSupplierDetailFormValue>>;
  setVenderSupplierDetailFormStatus: Dispatch<SetStateAction<TypeSupplierDetailFormStatus>>;
  setIsBusinessNumberValid: Dispatch<SetStateAction<boolean | null>>;
  setIsBusinessNumberExist: Dispatch<SetStateAction<boolean | null>>;
};

/**
 * [화주사정보관리 > 공급사관리] 공급사 상세 모달 컴포넌트 > 기본 정보 영역
 */
const VenderSupplierDetailModalDefaultInfoSection = ({
  errorFieldRefs,
  ownerSupplierCode,
  venderSupplierDetailForm,
  venderSupplierDetailFormStatus,
  isBusinessNumberValid,
  setVenderSupplierDetailForm,
  setVenderSupplierDetailFormStatus,
  setIsBusinessNumberValid,
  setIsBusinessNumberExist,
}: TypeVenderSupplierDetailModalDefaultInfoSectionProps) => {
  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setVenderSupplierDetailForm({
        ...venderSupplierDetailForm,
        zipCode,
        address,
      });
    },
  });
  const { getOwnerSupplierBusinessNumberCheck } = useGetOwnerSupplierBusinessNumberCheck();

  const { setIsLoading } = useLoadingStore();

  const fetchBusinessNumberCheck = async () => {
    try {
      setIsLoading(true);

      const isValid = await getOwnerSupplierBusinessNumberCheck({
        businessNumber: venderSupplierDetailForm.businessNumber,
      });

      setIsBusinessNumberValid(isValid);
      setVenderSupplierDetailFormStatus({
        ...venderSupplierDetailFormStatus,
        businessNumber: DEFAULT_INPUT_STATUS,
      });

      if (isValid) {
        toastService.addToast({
          message: '사업자번호가 확인되었습니다.',
        });
      } else {
        dialogService.addDialog({
          type: 'alert',
          children: (
            <>
              <p>사업자번호가 올바르지 않거나</p>
              <p>등록되지 않은 사업자입니다.</p>
            </>
          ),
          cancelText: '확인',
        });
      }
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderBusinessNumberValidIcon = useCallback(() => {
    if (isBusinessNumberValid === true) {
      return <CheckCircleFillIcon size={16} color="#029568" />;
    } else if (isBusinessNumberValid === false) {
      return <WarningFillIcon size={16} color="#FA622F" />;
    } else {
      return undefined;
    }
  }, [isBusinessNumberValid]);

  const handleClickBusinessNumberSearch = () => {
    fetchBusinessNumberCheck();
  };

  const resetVenderSupplierDetailFormState = (field: keyof TypeSupplierDetailFormStatus) => {
    setVenderSupplierDetailFormStatus({
      ...venderSupplierDetailFormStatus,
      [field]: DEFAULT_INPUT_STATUS,
    });
  };

  const handleChangeOwnerOriginSupplierCode = (e: ChangeEvent<HTMLInputElement>) =>
    setVenderSupplierDetailForm({
      ...venderSupplierDetailForm,
      ownerOriginSupplierCode: e.target.value,
    });

  const handleChangeBusinessNumber = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedValue = e.target.value;

    if (!validateNumberAndHyphenOnly(updatedValue)) return;

    setVenderSupplierDetailForm({
      ...venderSupplierDetailForm,
      businessNumber: formatBusinessNumber(updatedValue),
    });
    setIsBusinessNumberValid(null);
    setIsBusinessNumberExist(null);
    resetVenderSupplierDetailFormState('businessNumber');
  };

  const handleChangeSupplierName = (e: ChangeEvent<HTMLInputElement>) => {
    setVenderSupplierDetailForm({ ...venderSupplierDetailForm, supplierName: e.target.value });
    resetVenderSupplierDetailFormState('supplierName');
  };

  const handleChangeBusinessConditions = (e: ChangeEvent<HTMLInputElement>) =>
    setVenderSupplierDetailForm({
      ...venderSupplierDetailForm,
      businessConditions: e.target.value,
    });

  const handleChangeBusinessCategory = (e: ChangeEvent<HTMLInputElement>) =>
    setVenderSupplierDetailForm({
      ...venderSupplierDetailForm,
      businessCategory: e.target.value,
    });

  const handleChangeCeoName = (e: ChangeEvent<HTMLInputElement>) =>
    setVenderSupplierDetailForm({ ...venderSupplierDetailForm, ceoName: e.target.value });

  const handleChangeDetailAddress = (e: ChangeEvent<HTMLInputElement>) =>
    setVenderSupplierDetailForm({ ...venderSupplierDetailForm, detailAddress: e.target.value });

  const handleChangeTel = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedValue = e.target.value;

    if (validateNumberAndHyphenOnly(updatedValue)) {
      setVenderSupplierDetailForm({ ...venderSupplierDetailForm, tel: updatedValue });
      resetVenderSupplierDetailFormState('tel');
    }
  };

  const handleChangeFax = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedValue = e.target.value;

    if (validateNumberAndHyphenOnly(updatedValue)) {
      setVenderSupplierDetailForm({ ...venderSupplierDetailForm, fax: updatedValue });
      resetVenderSupplierDetailFormState('fax');
    }
  };

  const handleChangeDealStartDate = (date: Date | null) =>
    setVenderSupplierDetailForm({ ...venderSupplierDetailForm, dealStartDate: date });

  const handleChangeDealEndDate = (date: Date | null) =>
    setVenderSupplierDetailForm({ ...venderSupplierDetailForm, dealEndDate: date });

  const handleChangeUseStatus = (e: ChangeEvent<HTMLInputElement>) =>
    setVenderSupplierDetailForm({
      ...venderSupplierDetailForm,
      isUseStatus: e.target.value === 'Y',
    });

  return (
    <div className={styles['supplier-info-section']}>
      <Text size="xxlarge" weight="bold">
        기본 정보
      </Text>
      <div className={styles['input-rows-wrapper']}>
        <FormField
          label="공급사코드"
          contents={<Input className={styles['input']} type="text" size="small" disabled value={ownerSupplierCode} />}
        />
        <FormField
          label="화주사 공급사코드"
          tooltip={{
            description: (
              <p className={styles['owner-supplier-code-tooltip']}>
                화주사에서 자체 사용중인 공급사코드가 있는 경우 참고 정보로 입력 가능합니다.
              </p>
            ),
            maxWidth: '257px',
          }}
          contents={
            <Input
              className={styles['input']}
              type="text"
              size="small"
              placeholder="입력"
              value={venderSupplierDetailForm.ownerOriginSupplierCode}
              onChange={handleChangeOwnerOriginSupplierCode}
            />
          }
        />
        <FormField
          label="사업자번호"
          required
          tooltip={{
            description: (
              <div className={styles['business-number-tooltip']}>
                <p>아래의 경우 사업자 정보 조회가 불가능합니다.</p>
                <div>
                  <p className={styles['business-number-tooltip-detail']}>
                    <Bullet fill="#ffffff" /> 2인 미만의 사업장
                  </p>
                  <p className={styles['business-number-tooltip-detail']}>
                    <Bullet fill="#ffffff" /> 화주사의 사업자정보(API정보) 등록 이전
                  </p>
                </div>
              </div>
            ),
            maxWidth: '257px',
          }}
          contents={
            <>
              <Input
                ref={(el) => (errorFieldRefs.current.businessNumber = el)}
                className={styles['input']}
                type="text"
                size="small"
                placeholder="숫자만 입력 (- 없이)"
                rightIcon={renderBusinessNumberValidIcon()}
                status={venderSupplierDetailFormStatus.businessNumber.status}
                validationMessage={venderSupplierDetailFormStatus.businessNumber.message}
                value={venderSupplierDetailForm.businessNumber}
                onClick={() => resetVenderSupplierDetailFormState('businessNumber')}
                onChange={handleChangeBusinessNumber}
              />
              <Button
                className={styles['button']}
                color="secondary"
                size="small"
                disabled={venderSupplierDetailForm.businessNumber.length === 0}
                onClick={handleClickBusinessNumberSearch}
              >
                조회
              </Button>
            </>
          }
        />
        <FormField
          label="공급사명"
          required
          contents={
            <Input
              ref={(el) => (errorFieldRefs.current.supplierName = el)}
              className={styles['input']}
              type="text"
              size="small"
              placeholder="입력"
              status={venderSupplierDetailFormStatus.supplierName.status}
              validationMessage={venderSupplierDetailFormStatus.supplierName.message}
              value={venderSupplierDetailForm.supplierName}
              onClick={() => resetVenderSupplierDetailFormState('supplierName')}
              onChange={handleChangeSupplierName}
            />
          }
        />
        <FormField
          label="업태"
          contents={
            <Input
              className={styles['input']}
              type="text"
              size="small"
              placeholder="입력"
              value={venderSupplierDetailForm.businessConditions}
              onChange={handleChangeBusinessConditions}
            />
          }
        />
        <FormField
          label="업종"
          contents={
            <Input
              className={styles['input']}
              type="text"
              size="small"
              placeholder="입력"
              value={venderSupplierDetailForm.businessCategory}
              onChange={handleChangeBusinessCategory}
            />
          }
        />
        <FormField
          label="대표자명"
          contents={
            <Input
              className={styles['input']}
              type="text"
              size="small"
              placeholder="입력"
              value={venderSupplierDetailForm.ceoName}
              onChange={handleChangeCeoName}
            />
          }
        />
      </div>
      <Divider color="gray100" />
      <div className={styles['input-rows-wrapper']}>
        <FormField
          label="우편번호"
          contents={
            <>
              <Input
                className={styles['input']}
                type="text"
                size="small"
                disabled
                value={venderSupplierDetailForm.zipCode ?? ''}
              />
              <Button className={styles['button']} color="secondary" size="small" onClick={handleZipCodeSearchOpen}>
                검색
              </Button>
            </>
          }
        />
        <FormField
          label="기본주소"
          contents={
            <Input
              className={styles['input-long']}
              type="text"
              size="small"
              disabled
              value={venderSupplierDetailForm.address}
            />
          }
        />
        <FormField
          label="상세주소"
          contents={
            <Input
              className={styles['input-long']}
              type="text"
              size="small"
              placeholder="입력"
              value={venderSupplierDetailForm.detailAddress}
              onChange={handleChangeDetailAddress}
            />
          }
        />
        <FormField
          label="대표번호"
          contents={
            <Input
              ref={(el) => (errorFieldRefs.current.tel = el)}
              className={styles['input']}
              type="text"
              size="small"
              placeholder="- 포함 입력"
              status={venderSupplierDetailFormStatus.tel.status}
              validationMessage={venderSupplierDetailFormStatus.tel.message}
              value={venderSupplierDetailForm.tel}
              onClick={() => resetVenderSupplierDetailFormState('tel')}
              onChange={handleChangeTel}
            />
          }
        />
        <FormField
          label="FAX번호"
          contents={
            <Input
              ref={(el) => (errorFieldRefs.current.fax = el)}
              className={styles['input']}
              type="text"
              size="small"
              placeholder="- 포함 입력"
              status={venderSupplierDetailFormStatus.fax.status}
              validationMessage={venderSupplierDetailFormStatus.fax.message}
              value={venderSupplierDetailForm.fax}
              onClick={() => resetVenderSupplierDetailFormState('fax')}
              onChange={handleChangeFax}
            />
          }
        />
        <FormField
          label="거래일자"
          contents={
            <>
              <Text size="large">시작일</Text>
              <DatePicker
                className={styles['date-picker']}
                size="small"
                placeholder="날짜 선택"
                value={venderSupplierDetailForm.dealStartDate}
                onChange={handleChangeDealStartDate}
              />
              <Divider className={styles['divider']} orientation="vertical" />
              <Text size="large">종료일</Text>
              <DatePicker
                className={styles['date-picker']}
                size="small"
                placeholder="날짜 선택"
                value={venderSupplierDetailForm.dealEndDate}
                onChange={handleChangeDealEndDate}
              />
            </>
          }
          contentsClassName={styles['date-pickers-wrapper']}
        />
        <FormField
          label="사용여부"
          required
          contents={
            <RadioGroup
              className={styles['operation-radio-group']}
              name="operation-radio-group"
              size="medium"
              value={venderSupplierDetailForm.isUseStatus ? 'Y' : 'N'}
              onChange={handleChangeUseStatus}
            >
              <Radio label="사용" value={'Y'} />
              <Radio label="사용안함" value={'N'} />
            </RadioGroup>
          }
          contentsClassName={styles['radios-wrapper']}
        />
      </div>
    </div>
  );
};

export default VenderSupplierDetailModalDefaultInfoSection;
